'use client';

/**
 * Embed wrapper for the storefront. Renders inside a third-party iframe.
 *
 * Responsibilities:
 *   1. ResizeObserver → postMessage `eazybooking:resize` to parent every
 *      time content height changes. Parent's loader (embed.js) listens
 *      and adjusts the iframe's `height` attribute so the iframe never
 *      gets its own scrollbar — the parent page scrolls naturally.
 *   2. Forward booking lifecycle events to the parent so the host site
 *      can fire its own conversion pixels (Google Ads, Meta, GA4).
 *   3. iOS Stripe escape hatch — Safari sandboxes third-party iframes'
 *      storage access, breaking Stripe Elements. When checkout opens,
 *      we postMessage the parent and ask it to top-level-navigate to
 *      Stripe Hosted Checkout. (Implemented in InlinePlanPicker / the
 *      Storefront's Stripe wire-up — this shell only sets up the bridge.)
 *   4. Theming: accept `eazybooking:theme` messages from the parent and
 *      apply them as CSS custom properties at the iframe root.
 *
 * Why a Provider/Shell and not a hook: postMessage handlers must live in
 * a layout-level component so they survive page navigation inside the
 * storefront. ResizeObserver also needs to watch a stable container ref.
 *
 * Cross-frame messaging convention:
 *   - All messages from us are namespaced `eazybooking:*` so the parent
 *     can filter them.
 *   - We do NOT trust the `event.origin` of incoming messages for opt-in
 *     features (theme) — we only honour them; the parent's domain is
 *     enforced upstream by the iframe's `Content-Security-Policy:
 *     frame-ancestors` header.
 */

import { useEffect, useRef, type ReactNode } from 'react';

interface StorefrontEmbedShellProps {
  /** Used as a per-render distinct-id when emitting events upstream. */
  tenantId: string;
  children: ReactNode;
}

// Stable event-name prefix. Parent's loader filters on this exact string.
const NS = 'eazybooking';

interface OutboundResize {
  type: `${typeof NS}:resize`;
  height: number;
}
interface OutboundReady {
  type: `${typeof NS}:ready`;
  tenantId: string;
}
interface OutboundBookingStarted {
  type: `${typeof NS}:booking_started`;
}
interface OutboundBookingCompleted {
  type: `${typeof NS}:booking_completed`;
  bookingId: string;
  value?: number;
  currency?: string;
}
interface OutboundRequestRedirect {
  type: `${typeof NS}:request_redirect`;
  url: string;
}

type OutboundMessage =
  | OutboundResize
  | OutboundReady
  | OutboundBookingStarted
  | OutboundBookingCompleted
  | OutboundRequestRedirect;

interface InboundTheme {
  type: `${typeof NS}:theme`;
  primaryColor?: string;
  fontFamily?: string;
  radius?: number;
}

function post(message: OutboundMessage) {
  if (typeof window === 'undefined' || window.parent === window) return;
  try {
    // `'*'` rather than a specific origin: the parent enforces who can
    // load us via CSP frame-ancestors; we don't need to know their origin
    // up-front (would require a config round-trip), and the data we send
    // is non-sensitive (no PII beyond the booking id the host already has).
    window.parent.postMessage(message, '*');
  } catch (err) {
    // postMessage can throw if the message is unstructured-cloneable.
    // Capture to Sentry so we notice if a new message shape breaks the
    // serializer in the wild; never re-throw, embed must not crash the host.
    void import('@/lib/sentry')
      .then((mod) => {
        if ('captureSentryException' in mod) {
          (mod as { captureSentryException?: (e: unknown, ctx: unknown) => void })
            .captureSentryException?.(err, {
              level: 'warning',
              tags: { surface: 'embed-shell', op: 'postMessage' },
            });
        }
      })
      .catch(() => {
        /* sentry module unavailable — swallow */
      });
  }
}

export function StorefrontEmbedShell({ tenantId, children }: StorefrontEmbedShellProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Owner-configured fallbacks for booking_completed events. Fetched once
  // at mount from the public-config endpoint. Stored in a ref because we
  // only need them inside event handlers (no re-render coupling).
  const defaultsRef = useRef<{ value: number | null; currency: string }>({
    value: null,
    currency: 'EUR',
  });

  // GDPR consent gate. The host's loader passes `?consent=denied` in the
  // iframe src when the user has refused marketing cookies on the host
  // page. We mirror that decision into our own analytics: PostHog never
  // initialises, no event ever fires. Booking still works — we just
  // operate consent-free (cookies for the booking session itself are
  // strictly necessary and excluded from this gate).
  const consentDenied = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      consentDenied.current =
        new URL(window.location.href).searchParams.get('consent') === 'denied';
    } catch {
      consentDenied.current = false;
    }
  }, []);

  useEffect(() => {
    if (!tenantId) return;
    fetch(`/api/embed/public-config?tenant_id=${encodeURIComponent(tenantId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (!cfg) return;
        defaultsRef.current = {
          value: typeof cfg.defaultValue === 'number' ? cfg.defaultValue : null,
          currency: typeof cfg.defaultCurrency === 'string' ? cfg.defaultCurrency : 'EUR',
        };
      })
      .catch(() => {
        /* best-effort — events still fire, just without enrichment */
      });
  }, [tenantId]);

  // Resize observer — fires on every content height change. We debounce
  // implicitly via rAF so a fast-changing layout (e.g. accordion open)
  // doesn't spam the parent.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof window === 'undefined') return;

    let pending = 0;
    let lastHeight = 0;

    const emit = () => {
      pending = 0;
      const h = Math.ceil(root.getBoundingClientRect().height);
      if (h !== lastHeight && h > 0) {
        lastHeight = h;
        post({ type: `${NS}:resize`, height: h });
      }
    };

    const schedule = () => {
      if (pending) return;
      pending = window.requestAnimationFrame(emit);
    };

    const observer = new ResizeObserver(schedule);
    observer.observe(root);
    // Initial measurement (ResizeObserver fires on observe so this is
    // technically redundant, but explicit beats implicit).
    schedule();

    return () => {
      observer.disconnect();
      if (pending) window.cancelAnimationFrame(pending);
    };
  }, []);

  // Ready signal — tells the parent the iframe loaded successfully.
  // Parent can flip its placeholder skeleton off and start listening for
  // booking events. Analytics fire conditional on consent: when host
  // user has denied marketing cookies, we skip PostHog entirely.
  useEffect(() => {
    post({ type: `${NS}:ready`, tenantId });
    if (consentDenied.current) return;
    try {
      void import('@/lib/analytics/posthog').then((mod) => {
        if (typeof mod.trackEvent === 'function') {
          mod.trackEvent('embed_loaded', {
            tenantId,
            parent_origin:
              typeof document !== 'undefined' && document.referrer
                ? new URL(document.referrer).origin
                : 'unknown',
          });
        }
      });
    } catch {
      /* tracking is best-effort */
    }
  }, [tenantId]);

  // Inbound: theme messages from the parent. We write the CSS variables
  // both namespaced (`--embed-*`) and on the legacy `--primary-color`
  // root so any current OR future storefront component that adopts
  // `var(--primary-color)` instead of inline `tenant.primary_color`
  // picks up the override automatically.
  //
  // KNOWN LIMITATION: Most storefront components today set colors via
  // inline `style={{ backgroundColor: tenant.primary_color }}` rather
  // than CSS vars, so the postMessage-override won't visually flow
  // through. The plumbing here is correct + forward-compatible; a
  // separate refactor will migrate the inline styles to var-based.
  useEffect(() => {
    const onMessage = (event: MessageEvent<unknown>) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      const msg = data as InboundTheme;
      if (msg.type !== `${NS}:theme`) return;
      const root = document.documentElement;
      // Light validation — values that crossed an iframe boundary are
      // untrusted input. Reject anything that doesn't look like a
      // CSS color or font-name to keep injected `<style>` clean.
      const isHexOrRgb = (v: string) =>
        /^#[0-9a-f]{3,8}$/i.test(v) ||
        /^rgb\(.+\)$/i.test(v) ||
        /^hsl\(.+\)$/i.test(v);
      if (typeof msg.primaryColor === 'string' && isHexOrRgb(msg.primaryColor)) {
        root.style.setProperty('--embed-primary', msg.primaryColor);
        root.style.setProperty('--primary-color', msg.primaryColor);
      }
      if (
        typeof msg.fontFamily === 'string' &&
        /^[\w\s,'"\-]+$/.test(msg.fontFamily) &&
        msg.fontFamily.length < 200
      ) {
        root.style.setProperty('--embed-font-family', msg.fontFamily);
        document.body.style.fontFamily = msg.fontFamily;
      }
      if (typeof msg.radius === 'number' && Number.isFinite(msg.radius)) {
        const clamped = Math.max(0, Math.min(40, msg.radius));
        root.style.setProperty('--embed-radius', `${clamped}px`);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Booking lifecycle bridge — listen to internal app events and forward.
  // The storefront fires `eazybooking:internal:booking_started` and
  // `…:booking_completed` via `window.dispatchEvent(new CustomEvent(...))`
  // from its own success handler. This keeps the storefront code free of
  // postMessage logic; only this shell knows about cross-frame messaging.
  useEffect(() => {
    const onStarted = () => post({ type: `${NS}:booking_started` });
    const onCompleted = (e: Event) => {
      const detail = (e as CustomEvent<{
        bookingId: string;
        value?: number;
        currency?: string;
      }>).detail;
      if (!detail?.bookingId) return;
      // Enrich with owner-configured defaults if the service-level price
      // wasn't propagated. Tracking pixels with `value: undefined` are
      // silently ignored; the fallback ensures non-zero revenue numbers
      // hit Google Ads / Meta Pixel even for free/unpriced services.
      const value =
        typeof detail.value === 'number' && Number.isFinite(detail.value)
          ? detail.value
          : defaultsRef.current.value ?? undefined;
      const currency = detail.currency || defaultsRef.current.currency;
      post({
        type: `${NS}:booking_completed`,
        bookingId: detail.bookingId,
        value,
        currency,
      });
      // Mirror to PostHog with parent-origin so we can compare embed-
      // conversion rate per host site — but only if the host user
      // granted marketing consent. Booking itself still completes; we
      // just don't track the conversion in our analytics.
      if (consentDenied.current) return;
      try {
        void import('@/lib/analytics/posthog').then((mod) => {
          if (typeof mod.trackEvent === 'function') {
            mod.trackEvent('embed_booking_completed', {
              tenantId,
              bookingId: detail.bookingId,
              value,
              currency,
              parent_origin:
                typeof document !== 'undefined' && document.referrer
                  ? new URL(document.referrer).origin
                  : 'unknown',
            });
          }
        });
      } catch {
        /* best-effort */
      }
    };
    const onRedirect = (e: Event) => {
      const url = (e as CustomEvent<{ url: string }>).detail?.url;
      if (typeof url === 'string') post({ type: `${NS}:request_redirect`, url });
    };
    window.addEventListener(`${NS}:internal:booking_started`, onStarted);
    window.addEventListener(`${NS}:internal:booking_completed`, onCompleted);
    window.addEventListener(`${NS}:internal:request_redirect`, onRedirect);
    return () => {
      window.removeEventListener(`${NS}:internal:booking_started`, onStarted);
      window.removeEventListener(`${NS}:internal:booking_completed`, onCompleted);
      window.removeEventListener(`${NS}:internal:request_redirect`, onRedirect);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      data-eazybooking-embed-root="true"
      style={{ background: 'transparent' }}
    >
      {children}
    </div>
  );
}
